import {
  Alert,
  FacebookPage,
  TwitterAccount,
} from '@promoboxx/graphql-gateway-types'
import { History } from 'history'
import I18n from 'i18n-js'

import useManageBrandDialog from '@src/components/brands/useManageBrandDialog'
import colors from '@src/lib/Colors'

export interface NormalizedAlert {
  alert: Alert
  message: string
  badge?: () => React.ReactNode

  icon?: string | React.ReactNode
  iconSize?: number
  iconColor?: string
  iconBackgroundColor?: string

  onClick?: (context: AlertOnClickContext) => void

  groupKey?: string
  children?: NormalizedAlert[]
  showChildren?: boolean
}

export interface AlertOnClickContext {
  history: History
  showManageBrandDialog: ReturnType<
    typeof useManageBrandDialog
  >['showManageBrandDialog']
  showFacebookPermissionsDialog: (id?: string) => void
}

const normalizeAlert = (
  alert: Alert,
  includeNewAlerts = false,
): NormalizedAlert | null => {
  // Multiple ternaries just helps the type be `T | undefined`, instead
  // of `Maybe<T> | false | undefined`
  const brand = alert.retailer?.brand
    ? alert.retailer.brand
    : alert.subjectable?.__typename === 'Retailer' ||
        alert.subjectable?.__typename === 'EmailShare' ||
        alert.subjectable?.__typename === 'TwitterShare' ||
        alert.subjectable?.__typename === 'FacebookShare' ||
        alert.subjectable?.__typename === 'InstagramShare'
      ? alert.subjectable.brand
      : undefined
  const retailer = alert.retailer
    ? alert.retailer
    : alert.subjectable?.__typename === 'Retailer'
      ? alert.subjectable
      : undefined
  const retailerAccount = alert.retailer_account
    ? alert.retailer_account
    : alert.subjectable?.__typename === 'RetailerAccount'
      ? alert.subjectable
      : undefined

  const defaultGroupKey = `${alert.category}__${alert.subjectable_type}`
  const groupKeyRetailer = `${alert.category}__Retailer`
  // const groupKeyRetailerAccount = `${alert.category}__RetailerAccount`

  if (alert.category === 'facebook_page_invalid') {
    return {
      alert,
      icon: 'facebook',
      iconBackgroundColor: colors.facebook,
      message: (alert.subjectable as FacebookPage).name || '',
      groupKey: defaultGroupKey,
      onClick: (context) => {
        context.showFacebookPermissionsDialog(alert.subjectable?.id ?? '')
      },
    }
  }

  if (alert.category === 'twitter_account_invalid') {
    return {
      alert,
      icon: 'twitter',
      iconBackgroundColor: colors.twitter,
      message: I18n.t('alerts.twitterAccountInvalid', {
        nickname: (alert.subjectable as TwitterAccount).nickname,
      }),
      groupKey: defaultGroupKey,
      onClick: () => {
        if (alert.retailer_account?.slug) {
          goToLegacySocialAccounts(alert.retailer_account?.slug)
        }
      },
    }
  }

  if (alert.category === 'no_facebook_pages_connected') {
    return {
      alert,
      icon: 'facebook',
      iconBackgroundColor: colors.facebook,
      // If the alert is for a retailer account, it means they have no facebook
      // pages and no need to group them.
      groupKey:
        alert.subjectable?.__typename === 'RetailerAccount'
          ? undefined
          : groupKeyRetailer,
      // If we're grouped, only show the brand name. Otherwise show "no pages
      // connected."
      message:
        alert.subjectable?.__typename === 'RetailerAccount'
          ? I18n.t('alerts.noFacebookPageConnected')
          : brand?.name || '',
      onClick: (context) => {
        if (alert.subjectable?.__typename === 'RetailerAccount') {
          context.showFacebookPermissionsDialog()
        } else if (
          // TODO Need to check for slug because we've wrongly marked it as
          // optional in gql.
          retailer?.slug &&
          alert.subjectable?.__typename === 'Retailer'
        ) {
          context.showManageBrandDialog({
            retailerId: retailer.slug,
            initialTab: 'social',
            shouldHighlightMissing: true,
            validationMode: 'facebook',
            subtitle: I18n.t('manageBrands.alertSubtitles.connectFacebook'),
          })
        }
      },
    }
  }

  if (alert.category === 'no_twitter_accounts_connected') {
    return {
      alert,
      icon: 'twitter',
      iconBackgroundColor: colors.twitter,
      // If the alert is for a retailer account, it means they have no facebook
      // pages and no need to group them.
      groupKey:
        alert.subjectable?.__typename === 'RetailerAccount'
          ? undefined
          : groupKeyRetailer,
      // If we're grouped, only show the brand name. Otherwise show "no pages
      // connected."
      message:
        alert.subjectable?.__typename === 'RetailerAccount'
          ? I18n.t('alerts.noTwitterAccountsConnected')
          : brand?.name || '',
      onClick: (context) => {
        if (retailer?.slug) {
          context.showManageBrandDialog({
            retailerId: retailer.slug,
            initialTab: 'social',
            shouldHighlightMissing: true,
            validationMode: 'twitter',
            subtitle: I18n.t('manageBrands.alertSubtitles.connectTwitter'),
          })
        } else {
          window.location.href = `/#/${retailerAccount?.slug}/all/settings?tab=social_accounts`
        }
      },
    }
  }

  // TODO $50 for the first person to explain this alert and what we should do
  // for the user.
  if (alert.category === 'wallet_funds_expiring') {
    return {
      alert,
      icon: 'card',
      message: I18n.t('alerts.walletFundsExpiring'),
      groupKey: defaultGroupKey,
      showChildren: false,
    }
  }

  if (includeNewAlerts && alert.category === 'missing_location_on_retailer') {
    return {
      alert,
      icon: 'pin_off',
      iconBackgroundColor: colors.red,
      groupKey: defaultGroupKey,
      message:
        alert.subjectable?.__typename === 'RetailerAccount'
          ? retailerAccount?.name || ''
          : brand?.name || '',
      onClick: (context) => {
        if (retailer?.slug) {
          context.showManageBrandDialog({
            retailerId: retailer.slug,
            initialTab: 'locations',
            shouldHighlightMissing: true,
            validationMode: 'locations',
            subtitle: I18n.t('manageBrands.alertSubtitles.connectLocation'),
          })
        }
      },
    }
  }

  if (
    includeNewAlerts &&
    alert.category === 'missing_custom_field_values_for_retailer'
  ) {
    return {
      alert,
      icon: 'assignment',
      iconBackgroundColor: colors.orange,
      message: brand?.name || '',
      groupKey: defaultGroupKey,
      onClick: (context) => {
        if (retailer?.slug) {
          context.showManageBrandDialog({
            retailerId: retailer.slug,
            initialTab: 'custom_fields',
            shouldHighlightMissing: true,
            validationMode: 'custom_fields',
            subtitle: I18n.t('manageBrands.alertSubtitles.fillCustomFields'),
          })
        }
      },
    }
  }

  if (includeNewAlerts && alert.category === 'failed_facebook_share') {
    return {
      alert,
      icon: 'facebook',
      iconBackgroundColor: colors.facebook,
      message:
        alert.subjectable?.__typename === 'FacebookShare'
          ? alert.subjectable.campaign?.name || ''
          : '',
      groupKey: defaultGroupKey,
      onClick: () => {
        if (
          alert.retailer_account?.slug &&
          alert.subjectable?.__typename === 'FacebookShare' &&
          brand?.slug &&
          alert.subjectable.campaign?.link_id
        ) {
          goToCampaignInR1(
            alert.retailer_account?.slug,
            brand?.slug,
            alert.subjectable.campaign?.link_id,
            'facebook',
            alert.subjectable.share_setting_id,
          )
        }
      },
    }
  }

  if (includeNewAlerts && alert.category === 'failed_instagram_share') {
    return {
      alert,
      icon: 'instagram',
      iconBackgroundColor: colors.instagram,
      message:
        alert.subjectable?.__typename === 'InstagramShare'
          ? alert.subjectable.campaign?.name || ''
          : '',
      groupKey: defaultGroupKey,
      onClick: () => {
        if (
          alert.retailer_account?.slug &&
          alert.subjectable?.__typename === 'InstagramShare' &&
          brand?.slug &&
          alert.subjectable.campaign?.link_id
        ) {
          goToCampaignInR1(
            alert.retailer_account?.slug,
            brand?.slug,
            alert.subjectable.campaign?.link_id,
            'instagram',
            alert.subjectable.share_setting_id,
          )
        }
      },
    }
  }

  if (includeNewAlerts && alert.category === 'rate_limited_instagram_share') {
    return {
      alert,
      icon: 'instagram',
      iconBackgroundColor: colors.instagram,
      message:
        alert.subjectable?.__typename === 'InstagramShare'
          ? alert.subjectable.campaign?.name || ''
          : '',
      // We want to group these with other failed instagram shares, at least for
      // now.
      groupKey: 'failed_instagram_share__InstagramShare',
      onClick: () => {
        if (
          alert.retailer_account?.slug &&
          alert.subjectable?.__typename === 'InstagramShare' &&
          brand?.slug &&
          alert.subjectable.campaign?.link_id
        ) {
          goToCampaignInR1(
            alert.retailer_account?.slug,
            brand?.slug,
            alert.subjectable.campaign?.link_id,
            'instagram',
            alert.subjectable.share_setting_id,
          )
        }
      },
    }
  }

  if (includeNewAlerts && alert.category === 'failed_twitter_share') {
    return {
      alert,
      icon: 'twitter',
      iconBackgroundColor: colors.twitter,
      message:
        alert.subjectable?.__typename === 'TwitterShare'
          ? alert.subjectable.campaign?.name || ''
          : '',
      onClick: () => {
        if (
          alert.retailer_account?.slug &&
          alert.subjectable?.__typename === 'TwitterShare' &&
          brand?.slug &&
          alert.subjectable.campaign?.link_id
        ) {
          goToCampaignInR1(
            alert.retailer_account?.slug,
            brand?.slug,
            alert.subjectable.campaign?.link_id,
            'twitter',
            alert.subjectable.share_setting_id,
          )
        }
      },
    }
  }

  if (includeNewAlerts && alert.category === 'failed_email_share') {
    return {
      alert,
      icon: 'email_open',
      iconBackgroundColor: colors.orange,
      message:
        alert.subjectable?.__typename === 'EmailShare'
          ? alert.subjectable.campaign?.name || ''
          : '',
      onClick: () => {
        if (
          alert.retailer_account?.slug &&
          alert.subjectable?.__typename === 'EmailShare' &&
          brand?.slug &&
          alert.subjectable.campaign?.link_id
        ) {
          goToCampaignInR1(
            alert.retailer_account?.slug,
            brand?.slug,
            alert.subjectable.campaign?.link_id,
            'email',
          )
        }
      },
    }
  }

  if (includeNewAlerts && alert.category === 'retailer_created_auto_opt_in') {
    return {
      alert,
      icon: 'promoboxx',
      iconSize: 22,
      iconBackgroundColor: colors.blue_brand,
      message: brand?.name ?? '',
      groupKey: defaultGroupKey,
      onClick: (context) => {
        if (retailer && retailer.slug) {
          context.showManageBrandDialog({
            retailerId: retailer.slug,
            initialTab: 'social',
          })
        }
      },
    }
  }

  console.warn(`Not rendering alert with category ${alert.category}`, alert)

  return null
}

function goToLegacySocialAccounts(retailerAccountSlug: string) {
  window.location.href = `/#/${retailerAccountSlug}/all/settings?tab=social_accounts`
}

function goToCampaignInR1(
  retailerId: string,
  brandId: string,
  campaignId: string,
  shareType: string,
  shareSettingId?: string | number | undefined | null,
) {
  const shareSettingIdParam = shareSettingId
    ? `?shareSettingId=${shareSettingId}`
    : ''
  window.location.href = `/#/${retailerId}/${brandId}/campaigns/${campaignId}/activate/${shareType}${shareSettingIdParam}`
}

export default normalizeAlert
